import { useId, useState } from 'react'
import { Box, Button, Divider, Typography } from '@mui/material'
import Pen from '@assets/Analytics/Pen.svg'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  DEFAULT_TREE_MEMBER_ROLE,
  type TreeMemberRole,
} from '@/services/waterTreeMemberRoles'
import MemberRolePicker from './MemberRolePicker'

type Props = {
  selectedCount: number
  onChangeRole: (role: TreeMemberRole) => void | Promise<void>
}

export default function ChangeRolePanel({
  selectedCount,
  onChangeRole,
}: Props) {
  const titleId = useId()
  const [role, setRole] = useState<TreeMemberRole>(DEFAULT_TREE_MEMBER_ROLE)
  const [saving, setSaving] = useState(false)
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const hasSelection = selectedCount > 0

  const handleSave = async () => {
    setSaving(true)
    try {
      await onChangeRole(role)
    } finally {
      setSaving(false)
    }
  }

  const bgColor = isDarkMode ? '#1D263B' : '#FAFBFD'
  const textColor = isDarkMode ? '#FFFFFF' : '#003F61'
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: '14px',
        backgroundColor: bgColor,
        px: 3.5,
        py: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        direction: 'rtl',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Box component="img" src={Pen} alt="" sx={{ width: 18, height: 18 }} />
        <Typography
          id={titleId}
          sx={{
            fontSize: '18px',
            fontWeight: 200,
            color: textColor,
            fontFamily: 'Rubik',
          }}
        >
          שינוי תפקיד בעץ המים
        </Typography>
      </Box>

      <Divider sx={{ borderColor }} />

      <Typography
        sx={{
          fontSize: '15px',
          fontWeight: 200,
          lineHeight: 1.5,
          color: isDarkMode ? '#94A3B8' : '#64748B',
          fontFamily: 'Rubik',
        }}
      >
        {hasSelection
          ? `נבחרו ${selectedCount} צרכנים`
          : 'בחר צרכנים מהטבלה כדי לשנות את תפקידם.'}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <MemberRolePicker value={role} onChange={setRole} disabled={saving} />
        <Button
          disableElevation
          disabled={!hasSelection || saving}
          onClick={handleSave}
          sx={{
            backgroundColor: 'transparent',
            border: `1px solid ${hasSelection ? '#323F61' : borderColor}`,
            borderRadius: '8px',
            px: 2,
            height: 36,
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 200,
            color: textColor,
            fontFamily: 'Rubik',
            whiteSpace: 'nowrap',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid #4A5B85',
            },
            '&.Mui-disabled': {
              color: textColor,
              border: `1px solid ${borderColor}`,
              opacity: 0.3,
            },
          }}
        >
          עדכון תפקיד
        </Button>
      </Box>
    </Box>
  )
}
